<script setup lang="ts">
/**
 * Email verified (Figma `3603:68385` / `3603:68430` / `3603:68478`).
 *
 * Opened from the confirmation link (email 01). The confirmation itself runs in
 * the route handler, from the browser, never from a mail scanner's fetch.
 *  - success: alerts and marketing emails can be sent from now on.
 *  - expired: older than 24 hours or replaced by a newer link; Send a new link
 *    resends email 01 (signed out → Log in first), 60 s cooldown.
 *  - already: a used link opened again — calm tone, the goal is reached.
 */
import { computed, onBeforeUnmount, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { FairButton } from '@/ui/primitives/FairButton'
import { useFairToast } from '@/ui/patterns/FairToast'
import { useAuthModal } from '@/features/auth/store'
import AuthScreenShell from '@/features/auth/ui/parts/AuthScreenShell.vue'
import { useResendEmailVerificationMutation } from '@/mutations/profile/useAccountMutations'
import successUrl from '@/assets/img/auth/illustration-password-updated.png'
import expiredUrl from '@/assets/img/auth/illustration-link-expired.png'
import { useEmailLinkStore } from '../store'
import { useSignedInAction } from './useSignedInAction'

const RESEND_COOLDOWN_S = 60

const { t } = useI18n()
const toast = useFairToast()
const authModal = useAuthModal()
const links = useEmailLinkStore()
const runSignedIn = useSignedInAction()
const resend = useResendEmailVerificationMutation()

const status = computed(() => links.verifiedStatus)
const copyKey = computed(() => `email_links.verified.${status.value}`)

const cooldown = ref(0)
let timer: ReturnType<typeof setInterval> | undefined

function startCooldown() {
  cooldown.value = RESEND_COOLDOWN_S
  clearInterval(timer)
  timer = setInterval(() => {
    cooldown.value -= 1
    if (cooldown.value <= 0) clearInterval(timer)
  }, 1000)
}

onBeforeUnmount(() => clearInterval(timer))

async function sendNewLink() {
  try {
    await resend.mutateAsync()
    startCooldown()
    toast.show({ message: t('email_links.toast.link_sent') })
  } catch {
    toast.show({ message: t('email_links.errors.generic'), tone: 'warning' })
  }
}
</script>

<template>
  <AuthScreenShell
    centered
    :title="t(`${copyKey}.title`)"
    :description="t(`${copyKey}.description`)"
  >
    <template #art>
      <img
        class="fair-auth-screen__art"
        :src="status === 'expired' ? expiredUrl : successUrl"
        alt=""
      >
    </template>

    <template v-if="status === 'expired'">
      <FairButton
        class="fair-auth-cta"
        size="auto"
        :label="t('email_links.verified.expired.cta')"
        :loading="resend.isPending.value"
        :disabled="cooldown > 0"
        @click="runSignedIn(sendNewLink)"
      />
      <p class="fair-auth-switch">
        <button
          type="button"
          class="fair-auth-form__link"
          @click="authModal.showAuthModal('login')"
        >
          {{ t('auth.link_expired.back_to_login') }}
        </button>
      </p>
    </template>

    <FairButton
      v-else
      class="fair-auth-cta"
      size="auto"
      :label="t('email_links.continue')"
      @click="authModal.hideAuthModal()"
    />
  </AuthScreenShell>
</template>
